import { useContext, useMemo } from 'react';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import PowerSettingsNewOutlinedIcon from '@mui/icons-material/PowerSettingsNewOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import { TitleMediumEmphasized, BodySmall } from '@/design-system';
import { Divider, PageTitleHelmet } from '@/components';
import { APP_ROUTES } from '@/config/constants';
import { AccountCard } from './components/AccountCard/AccountCard';
import styles from './Account.styles';
import useProfile from '@/hooks/useProfile';
import { useReactiveVar } from '@apollo/client';
import { userInformationVar } from '@/graphql/state';
import UserPreferencesContext from '@/context/UserPreferencesContext';

export const Account: React.FC = () => {
  const { language } = useContext(UserPreferencesContext);
  const user = useReactiveVar(userInformationVar);
  const userName = useMemo(
    () => (user?.firstName && user?.lastName ? `${user?.firstName} ${user?.lastName}` : 'Parent'),
    [user]
  );

  const list = useMemo(
    () => [
      {
        icon: <AccountCircleOutlinedIcon />,
        label: 'Account Information',
        route: APP_ROUTES.accountInformation,
      },
      {
        icon: <TuneOutlinedIcon />,
        label: 'Preference',
        route: APP_ROUTES.preference,
      },
      {
        icon: <HelpOutlineOutlinedIcon />,
        label: 'Help',
        route: `https://info.peachjar.com/${language.code === 'es' ? 'es' : 'en'}/help-center`,
        isExternal: true,
      },
      {
        icon: <AssignmentOutlinedIcon />,
        label: 'Terms & Policies',
        route: 'https://peachjar.com/privacy-policy-terms.html#terms',
        isExternal: true,
      },
      {
        icon: <PowerSettingsNewOutlinedIcon />,
        label: 'Logout',
        route: APP_ROUTES.login,
      },
    ],
    [language]
  );

  return (
    <>
      <PageTitleHelmet>Account</PageTitleHelmet>
      <header className={styles.header} data-testid="accountInformationHeader">
        <TitleMediumEmphasized data-testid="userName">{userName}</TitleMediumEmphasized>
        <BodySmall data-testid="userEmail">{user?.email}</BodySmall>
      </header>
      <Divider />
      <main className={styles.container} data-testid="accountList">
        <div>
          {list.map(({ label, ...listProps }) => (
            <AccountCard label={label} {...listProps} key={label} showArrow={label !== 'Logout'} />
          ))}
        </div>
      </main>
    </>
  );
};
